import { useEffect, useState } from 'react';
import { plataforma } from '@/platform';
import { useConfiguracao } from '@/features/configuracoes/hooks/useConfiguracao';
import {
  configuracoesApi,
  diasValidadeLink,
  preferenciaTipoVeiculo,
  rotuloDias,
  VALIDADE_LINK_MAX_DIAS,
  VALIDADE_LINK_MIN_DIAS,
  type ChaveConfiguracao,
  type Conferencia,
  type DadosEmpresa,
  type PreferenciaTipoVeiculo,
} from '@/features/configuracoes/api/configuracoesApi';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { RichTextEditor } from '@/shared/ui/RichTextEditor';
import { Toast, type TipoToast } from '@/shared/ui/Toast';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { EtapasKanbanCard } from '@/features/configuracoes/pages/EtapasKanbanCard';

const LIMITE_HTML = 200_000;

function vazio(html: string): boolean {
  // O TipTap deixa "<p></p>" quando o usuário apaga tudo — tratamos como vazio.
  const limpo = html.replace(/<p>\s*<\/p>/g, '').trim();
  return limpo === '' || limpo === '<p></p>';
}

export function ConfiguracoesPage({ onAbrirLimpeza }: { onAbrirLimpeza: () => void }) {
  const [toast, setToast] = useState<{ mensagem: string; tipo: TipoToast } | null>(null);

  return (
    <section className="page">
      <PageHeader
        titulo="Configurações"
        descricao="Ajustes da sua empresa que se aplicam ao app desktop."
      />

      <Toast
        mensagem={toast?.mensagem ?? null}
        tipo={toast?.tipo}
        onFechar={() => setToast(null)}
      />

      <DadosEmpresaCard onToast={setToast} />

      <EtapasKanbanCard onToast={setToast} />

      <ValidadeLinkCard onToast={setToast} />

      <FotosCelularCard onToast={setToast} />

      <SincronizacaoCard onToast={setToast} />

      <LimparDadosCard onAbrir={onAbrirLimpeza} />

      <TermoCard
        chave="termo_impressao_a4"
        titulo="Termo personalizado da impressão A4"
        hint="Esse texto aparece no rodapé da folha A4 da ordem de serviço — logo abaixo dos totais e antes das assinaturas. Você pode usar negrito, itálico, sublinhado, listas, links e imagens (cole por URL ou envie um arquivo do computador). Deixe em branco para não imprimir nenhum termo."
        placeholder="Ex.: Garantia de 90 dias em peças e serviços…"
        onToast={setToast}
      />

      <TermoCard
        chave="termo_impressao_58mm"
        titulo="Termo personalizado do cupom 58mm"
        hint="Esse texto aparece no fim do cupom térmico 58mm, antes do rodapé. Como o cupom é estreito, prefira um texto curto. Deixe em branco para não imprimir nenhum termo."
        placeholder="Ex.: Garantia de 90 dias em peças e serviços."
        onToast={setToast}
      />
    </section>
  );
}

/**
 * Por quantos dias o link público de acompanhamento da O.S. (`/os/<token>`)
 * que a oficina manda ao cliente continua abrindo. Quem confere o token é
 * o servidor: no desktop o valor sobe pelo mesmo trilho dos termos de
 * impressão; no web grava direto na conta.
 */
function ValidadeLinkCard({
  onToast,
}: {
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const { valor, carregando, erro, recarregar } = useConfiguracao('validade_link_acompanhamento_dias');
  const salvo = String(diasValidadeLink(valor));
  const [rascunho, setRascunho] = useState(salvo);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!carregando) setRascunho(salvo);
  }, [salvo, carregando]);

  const alterou = rascunho.trim() !== salvo;
  const numero = Number(rascunho.trim());
  const valido =
    /^\d+$/.test(rascunho.trim()) &&
    numero >= VALIDADE_LINK_MIN_DIAS &&
    numero <= VALIDADE_LINK_MAX_DIAS;

  async function salvar() {
    if (!valido) {
      onToast({
        mensagem: `Informe um número inteiro de ${VALIDADE_LINK_MIN_DIAS} a ${VALIDADE_LINK_MAX_DIAS} dias.`,
        tipo: 'erro',
      });
      return;
    }
    setSalvando(true);
    try {
      await configuracoesApi.salvar('validade_link_acompanhamento_dias', String(numero));
      await recarregar();
      onToast({ mensagem: `Validade do link salva: ${rotuloDias(numero)}.`, tipo: 'sucesso' });
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao salvar.',
        tipo: 'erro',
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Link de acompanhamento</span>
      </div>
      <div className="card__body">
        {erro && (
          <p role="alert" className="alert alert--error">
            {erro}
          </p>
        )}
        <p className="hint" style={{ marginTop: 0 }}>
          Por quantos dias o link de acompanhamento da ordem de serviço que
          você envia ao cliente continua abrindo. Depois disso, a página
          mostra um botão para o cliente pedir um link novo pelo WhatsApp da
          empresa (o telefone cadastrado em Dados da empresa).
        </p>
        {carregando ? (
          <p className="hint">Carregando…</p>
        ) : (
          <div style={{ maxWidth: 240 }}>
            <Field
              rotulo="Validade do link (dias)"
              obrigatorio
              dica={`De ${VALIDADE_LINK_MIN_DIAS} a ${VALIDADE_LINK_MAX_DIAS} dias. Padrão: 1 dia.`}
            >
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="number"
                  inputMode="numeric"
                  min={VALIDADE_LINK_MIN_DIAS}
                  max={VALIDADE_LINK_MAX_DIAS}
                  step={1}
                  required
                  value={rascunho}
                  onChange={(e) => setRascunho(e.target.value)}
                  aria-invalid={!valido}
                />
              )}
            </Field>
          </div>
        )}
        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button onClick={() => setRascunho(salvo)} disabled={!alterou || salvando}>
            Descartar
          </Button>
          <Button
            variante="primary"
            onClick={() => void salvar()}
            disabled={!alterou || salvando || carregando}
          >
            {salvando ? 'Salvando…' : 'Salvar validade'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const OPCOES_TIPO_VEICULO: Array<{ valor: PreferenciaTipoVeiculo; rotulo: string; aviso: string }> = [
  { valor: 'carro', rotulo: 'Carro', aviso: 'só o carro' },
  { valor: 'moto', rotulo: 'Moto', aviso: 'só a moto' },
  { valor: 'ambos', rotulo: 'Carro e moto', aviso: 'carro e moto, com escolha no celular' },
];

/**
 * Qual desenho a página do celular mostra nas fotos do veículo. Quem
 * monta a página é o servidor: no desktop o valor sobe pelo mesmo trilho
 * dos termos de impressão; no web grava direto na conta.
 */
function FotosCelularCard({
  onToast,
}: {
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const { valor, carregando, erro, recarregar } = useConfiguracao('captura_tipo_veiculo');
  const salvo = preferenciaTipoVeiculo(valor);
  const [salvando, setSalvando] = useState<PreferenciaTipoVeiculo | null>(null);
  // O recarregar depois de salvar não pode piscar o "Carregando…".
  const [carregou, setCarregou] = useState(false);
  const atual = salvando ?? salvo;

  useEffect(() => {
    if (!carregando) setCarregou(true);
  }, [carregando]);

  async function escolher(opcao: (typeof OPCOES_TIPO_VEICULO)[number]) {
    if (opcao.valor === salvo || salvando) return;
    setSalvando(opcao.valor);
    try {
      await configuracoesApi.salvar('captura_tipo_veiculo', opcao.valor);
      await recarregar();
      onToast({ mensagem: `Fotos pelo celular: ${opcao.aviso}.`, tipo: 'sucesso' });
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao salvar.',
        tipo: 'erro',
      });
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title" id="fotos-celular-titulo">
          Fotos pelo celular
        </span>
      </div>
      <div className="card__body">
        {erro && (
          <p role="alert" className="alert alert--error">
            {erro}
          </p>
        )}
        <p className="hint" style={{ marginTop: 0 }}>
          Qual veículo aparece no desenho quando o mecânico lê o QR code da
          ordem de serviço para fotografar. O celular mostra só o desenho
          escolhido; com "Carro e moto", aparece também o seletor Carro/Moto.
        </p>
        {!carregou ? (
          <p className="hint">Carregando…</p>
        ) : (
          <div
            className="segmented segmented--grande"
            role="radiogroup"
            aria-labelledby="fotos-celular-titulo"
          >
            {OPCOES_TIPO_VEICULO.map((opcao) => (
              <button
                key={opcao.valor}
                type="button"
                role="radio"
                aria-checked={atual === opcao.valor}
                disabled={salvando !== null}
                className={`segmented__item${atual === opcao.valor ? ' segmented__item--ativo' : ''}`}
                onClick={() => void escolher(opcao)}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TermoCard({
  chave,
  titulo,
  hint,
  placeholder,
  onToast,
}: {
  chave: ChaveConfiguracao;
  titulo: string;
  hint: string;
  placeholder: string;
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const { valor, carregando, erro, recarregar } = useConfiguracao(chave);
  const [rascunho, setRascunho] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!carregando) setRascunho(valor ?? '');
  }, [valor, carregando]);

  const alterou = rascunho !== (valor ?? '');

  async function salvar() {
    if (rascunho.length > LIMITE_HTML) {
      onToast({
        mensagem: `Conteúdo excede ${LIMITE_HTML.toLocaleString('pt-BR')} caracteres (HTML).`,
        tipo: 'erro',
      });
      return;
    }
    setSalvando(true);
    try {
      const aSalvar = vazio(rascunho) ? null : rascunho;
      await configuracoesApi.salvar(chave, aSalvar);
      await recarregar();
      onToast({ mensagem: 'Termo salvo.', tipo: 'sucesso' });
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao salvar.',
        tipo: 'erro',
      });
    } finally {
      setSalvando(false);
    }
  }

  function descartar() {
    setRascunho(valor ?? '');
  }

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">{titulo}</span>
      </div>
      <div className="card__body">
        {erro && (
          <p role="alert" className="alert alert--error">
            {erro}
          </p>
        )}

        <p className="hint" style={{ marginTop: 0 }}>
          {hint}
        </p>

        {carregando ? (
          <p className="hint">Carregando…</p>
        ) : (
          <RichTextEditor
            valor={rascunho}
            onChange={setRascunho}
            maxCaracteresHtml={LIMITE_HTML}
            placeholder={placeholder}
          />
        )}

        <div
          className="hint"
          style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}
        >
          <span>
            {rascunho.length.toLocaleString('pt-BR')} /{' '}
            {LIMITE_HTML.toLocaleString('pt-BR')} caracteres (HTML)
          </span>
          {alterou && <span>● Alterações não salvas</span>}
        </div>

        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button onClick={descartar} disabled={!alterou || salvando}>
            Descartar
          </Button>
          <Button
            variante="primary"
            onClick={() => void salvar()}
            disabled={!alterou || salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar termo'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SincronizacaoCard({
  onToast,
}: {
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  /** Tipo cuja linha está baixando agora — null quando nenhuma. */
  const [baixandoTipo, setBaixandoTipo] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [conferencia, setConferencia] = useState<Conferencia | null>(null);
  const dialogos = useDialogos();

  // "Reenviar pendentes" e "Baixar tudo do servidor" só existem porque o
  // desktop tem fila local e cursor de pull — o web não tem modo offline
  // (decisão de produto, não lacuna a preencher). Ver
  // `src/platform/tipos.ts:CapacidadesPlataforma.ferramentasSync`.
  if (!plataforma.capacidades.ferramentasSync) return null;

  /**
   * Compara o que o servidor tem com o que existe nesta máquina.
   *
   * O pull já roda sozinho (boot, aviso do WebSocket, depois de cada
   * push), mas nada dizia se deu conta — e dois limites conhecidos tornam
   * a dúvida legítima: o dump inicial trunca acima de 130 mil entidades
   * sem avisar, e o pull incremental para em 50 mil eventos por execução.
   */
  async function conferir(silencioso = false) {
    setConferindo(true);
    try {
      const r = await configuracoesApi.conferirSincronizacao();
      setConferencia(r);
      if (silencioso) return r;
      if (r.tipo === 'sem-sessao') {
        onToast({ mensagem: 'Faça login para conferir com o servidor.', tipo: 'erro' });
      } else if (r.tipo === 'falha') {
        onToast({ mensagem: `Não deu para conferir: ${r.motivo}`, tipo: 'erro' });
      } else if (r.faltando === 0 && !r.atrasado) {
        onToast({ mensagem: 'Está tudo aqui — nada faltando do servidor.', tipo: 'sucesso' });
      }
      return r;
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao conferir.',
        tipo: 'erro',
      });
      return null;
    } finally {
      setConferindo(false);
    }
  }

  /**
   * Baixa só a linha escolhida da conferência. Existe porque "Baixar
   * tudo" é desproporcional quando falta pouco: buscar 5 produtos não
   * deveria trazer de volta 4 mil movimentações que já estão aqui.
   */
  async function baixarSomenteTipo(entidadeTipo: string, rotulo: string) {
    setBaixandoTipo(entidadeTipo);
    try {
      const r = await configuracoesApi.baixarTipo(entidadeTipo);
      if (r.tipo === 'ok') {
        onToast({
          mensagem: `${rotulo}: ${r.registros} registro(s) recebido(s) do servidor.`,
          tipo: 'sucesso',
        });
        await conferir(true);
      } else if (r.tipo === 'sem-sessao') {
        onToast({ mensagem: 'Faça login antes de baixar do servidor.', tipo: 'erro' });
      } else {
        onToast({ mensagem: `Falha ao baixar ${rotulo}: ${r.motivo}`, tipo: 'erro' });
      }
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : `Falha ao baixar ${rotulo}.`,
        tipo: 'erro',
      });
    } finally {
      setBaixandoTipo(null);
    }
  }

  async function baixarTudoDoServidor() {
    const ok = await dialogos.confirmar({
      titulo: 'Baixar tudo do servidor',
      mensagem:
        'Vamos baixar o estado atual da sua conta no servidor e gravar por cima do banco local. Cadastros locais que ainda não foram sincronizados ficam intactos, e uma cópia do banco atual é guardada antes de começar.\n\nContinuar?',
      textoConfirmar: 'Baixar',
    });
    if (!ok) return;
    setBaixando(true);
    try {
      const r = await configuracoesApi.forcarRedump();
      if (r.tipo === 'ok') {
        onToast({
          mensagem:
            `Sincronização completa: ${r.entidades} registro(s) recebido(s) do servidor.` +
            (r.copiaSeguranca ? ' Cópia do banco anterior guardada.' : ''),
          tipo: 'sucesso',
        });
      } else if (r.tipo === 'sem-sessao') {
        onToast({ mensagem: 'Faça login antes de baixar do servidor.', tipo: 'erro' });
      } else {
        onToast({ mensagem: `Falha ao baixar: ${r.motivo}`, tipo: 'erro' });
      }
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao baixar do servidor.',
        tipo: 'erro',
      });
    } finally {
      setBaixando(false);
    }
  }

  async function reenviar() {
    setEnviando(true);
    try {
      const r = await configuracoesApi.reenviarPendentes();
      if (r.total === 0) {
        onToast({ mensagem: 'Nada pendente para reenviar.', tipo: 'sucesso' });
        return;
      }
      const partes: string[] = [];
      if (r.clientes) partes.push(`${r.clientes} cliente(s)`);
      if (r.produtos) partes.push(`${r.produtos} produto(s)`);
      if (r.servicos) partes.push(`${r.servicos} serviço(s)`);
      if (r.colaboradores) partes.push(`${r.colaboradores} colaborador(es)`);
      if (r.veiculos) partes.push(`${r.veiculos} veículo(s)`);
      const detalhe = partes.join(', ');
      if (r.push.tipo === 'ok') {
        onToast({
          mensagem: `Reenviado: ${detalhe}. Servidor confirmou ${r.push.enviados} novo(s) e ignorou ${r.push.ignorados} já conhecido(s).`,
          tipo: 'sucesso',
        });
      } else if (r.push.tipo === 'sem-sessao') {
        onToast({
          mensagem: `Enfileirado: ${detalhe}. Faça login para enviar ao servidor.`,
          tipo: 'erro',
        });
      } else if (r.push.tipo === 'falha') {
        onToast({
          mensagem: `Enfileirado: ${detalhe}. Falha ao enviar: ${r.push.motivo}. O worker tentará novamente.`,
          tipo: 'erro',
        });
      } else {
        onToast({ mensagem: `Reenfileirado: ${detalhe}.`, tipo: 'sucesso' });
      }
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao reenviar pendentes.',
        tipo: 'erro',
      });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Sincronização</span>
      </div>
      <div className="card__body">
        <p className="hint" style={{ marginTop: 0 }}>
          Se cadastros antigos estão presos no badge ⏳ "Aguardando servidor"
          (por exemplo após importação direta no banco), use este botão para
          reenfileirar os eventos e empurrá-los pro servidor agora. É seguro
          repetir — registros que o servidor já conhece são ignorados.
        </p>
        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button
            variante="primary"
            onClick={() => void reenviar()}
            disabled={enviando || baixando}
          >
            {enviando ? 'Reenviando…' : 'Reenviar pendentes'}
          </Button>
        </div>

        <hr className="separador-secao" />

        <p className="hint" style={{ marginTop: 0 }}>
          <strong>Conferir com o servidor</strong> compara, item a item, o que
          está lá com o que existe nesta máquina. Serve para responder
          "está tudo aqui?" sem depender de conferir tela por tela.
        </p>
        <p className="hint">
          Depois de conferir, cada linha ganha um botão <strong>Baixar</strong>,
          que traz do servidor só aquele tipo de registro. É o caminho normal
          quando falta pouco: buscar 5 produtos não precisa trazer de volta as
          4 mil movimentações de estoque que já estão aqui.
        </p>
        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button
            onClick={() => void conferir()}
            disabled={conferindo || enviando || baixando || baixandoTipo !== null}
          >
            {conferindo ? 'Conferindo…' : 'Conferir com o servidor'}
          </Button>
        </div>

        {conferencia?.tipo === 'ok' && (
          <ResultadoConferencia
            dados={conferencia}
            baixandoTipo={baixandoTipo}
            ocupado={conferindo || enviando || baixando || baixandoTipo !== null}
            onBaixarTipo={(tipo, rotulo) => void baixarSomenteTipo(tipo, rotulo)}
          />
        )}

        <hr className="separador-secao" />

        <p className="hint" style={{ marginTop: 0 }}>
          <strong>Baixar tudo do servidor</strong> traz o estado completo da
          conta de uma vez, em vez de linha a linha. Use quando o problema não
          couber numa linha só — a máquina ficou muito tempo fora do ar, ou a
          conferência continua acusando falta depois de baixar os tipos.
        </p>
        <p className="hint">
          Ele faz duas coisas que o botão de cada linha não faz: traz também os
          dados da empresa e as configurações da conta, e marca esta máquina
          como em dia com o servidor. Por baixar tudo, é o mais demorado — e é
          por isso que guarda uma cópia do banco atual antes de começar.
          Registros criados aqui que ainda não subiram ficam intactos nos dois
          casos.
        </p>
        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button
            onClick={() => void baixarTudoDoServidor()}
            disabled={enviando || baixando || conferindo || baixandoTipo !== null}
          >
            {baixando ? 'Baixando…' : 'Baixar tudo do servidor'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Resultado da conferência.
 *
 * Diferença positiva é o que falta baixar. Negativa NÃO é erro: é dado
 * criado aqui que ainda não subiu — o worker de push cuida disso, e
 * chamar de "problema" faria o operador tentar consertar o que já está
 * a caminho.
 */
function ResultadoConferencia({
  dados,
  baixandoTipo,
  ocupado,
  onBaixarTipo,
}: {
  dados: Extract<Conferencia, { tipo: 'ok' }>;
  /** Tipo cuja linha está baixando agora — null quando nenhuma. */
  baixandoTipo: string | null;
  /** Alguma operação de sync em andamento: trava todos os botões. */
  ocupado: boolean;
  onBaixarTipo: (entidadeTipo: string, rotulo: string) => void;
}) {
  const tudoCerto = dados.faltando === 0 && !dados.atrasado;

  return (
    <div className="conferencia" style={{ marginTop: 16 }}>
      <p
        className={`conferencia__veredito${tudoCerto ? '' : ' conferencia__veredito--atencao'}`}
        role="status"
      >
        {tudoCerto
          ? 'Está tudo aqui. Nada faltando do servidor.'
          : dados.faltando > 0
            ? `Faltam ${dados.faltando} registro(s) do servidor nesta máquina.`
            : 'Há alterações no servidor que esta máquina ainda não aplicou.'}
      </p>

      {!tudoCerto && (
        <p className="hint" style={{ marginTop: 4 }}>
          Use o botão <strong>Baixar</strong> na linha que estiver faltando. Se
          a falta estiver espalhada por várias linhas, "Baixar tudo do servidor"
          resolve de uma vez.
        </p>
      )}

      {dados.soLocal > 0 && (
        <p className="hint" style={{ marginTop: 4 }}>
          {dados.soLocal} registro(s) existem só aqui e ainda não subiram. Isso é
          normal logo após cadastrar algo offline — o envio acontece sozinho.
        </p>
      )}

      <div className="table-scroll" style={{ marginTop: 12 }}>
        <table className="table table--responsive">
          <thead>
            <tr>
              <th>O quê</th>
              <th style={{ textAlign: 'right' }}>No servidor</th>
              <th style={{ textAlign: 'right' }}>Nesta máquina</th>
              <th style={{ textAlign: 'right' }}>Diferença</th>
              <th style={{ textAlign: 'right' }}>
                <span className="sr-only">Baixar</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {dados.linhas.map((l) => (
              <tr key={l.entidadeTipo}>
                <td className="table__primary" data-label="O quê">
                  {l.rotulo}
                </td>
                <td data-label="No servidor" style={{ textAlign: 'right' }}>
                  {l.noServidor}
                </td>
                <td data-label="Nesta máquina" style={{ textAlign: 'right' }}>
                  {l.local}
                </td>
                <td
                  data-label="Diferença"
                  style={{ textAlign: 'right' }}
                  className={
                    l.diferenca > 0
                      ? 'conferencia__falta'
                      : l.diferenca < 0
                        ? 'conferencia__so-local'
                        : undefined
                  }
                >
                  {l.diferenca === 0
                    ? '—'
                    : l.diferenca > 0
                      ? `faltam ${l.diferenca}`
                      : `${-l.diferenca} só aqui`}
                </td>
                <td data-label="Baixar" style={{ textAlign: 'right' }}>
                  {/*
                    Disponível mesmo quando a diferença é zero: contagens
                    iguais não garantem conteúdo igual — uma edição feita
                    em outra máquina não cria registro novo.
                  */}
                  <Button
                    variante={l.diferenca > 0 ? 'primary' : 'ghost'}
                    onClick={() => onBaixarTipo(l.entidadeTipo, l.rotulo)}
                    disabled={ocupado}
                    title={`Baixar ${l.rotulo} do servidor`}
                  >
                    {baixandoTipo === l.entidadeTipo ? 'Baixando…' : 'Baixar'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="hint" style={{ marginTop: 8 }}>
        Conferido em {new Date(dados.geradoEm).toLocaleString('pt-BR')} ·{' '}
        {dados.totalEventosServidor} evento(s) no servidor.
      </p>
    </div>
  );
}

function LimparDadosCard({ onAbrir }: { onAbrir: () => void }) {
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Limpar dados</span>
      </div>
      <div className="card__body">
        <p className="hint" style={{ marginTop: 0 }}>
          Terminou o teste de apresentação e quer começar do zero? Aqui você
          apaga os dados por categoria — ordens de serviço, clientes, peças,
          serviços, financeiro, colaboradores — escolhendo o que sai e o que
          fica. Sua conta, sua assinatura e os dados da empresa não são
          afetados.
        </p>
        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button onClick={onAbrir}>Abrir limpeza de dados</Button>
        </div>
      </div>
    </div>
  );
}

const EMPRESA_VAZIA: DadosEmpresa = {
  nomeEmpresa: null,
  telefone: null,
  email: null,
  cnpj: null,
  inscricaoEstadual: null,
  endereco: null,
  numero: null,
  bairro: null,
  cidade: null,
  estado: null,
  cep: null,
};

function DadosEmpresaCard({
  onToast,
}: {
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const [dados, setDados] = useState<DadosEmpresa>(EMPRESA_VAZIA);
  const [original, setOriginal] = useState<DadosEmpresa>(EMPRESA_VAZIA);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const r = await configuracoesApi.obterEmpresa();
        if (!cancelado) {
          setDados(r);
          setOriginal(r);
        }
      } catch (e) {
        if (!cancelado) {
          onToast({
            mensagem: e instanceof Error ? e.message : 'Falha ao carregar dados.',
            tipo: 'erro',
          });
        }
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [onToast]);

  const alterou = JSON.stringify(dados) !== JSON.stringify(original);
  const [buscandoCep, setBuscandoCep] = useState(false);

  function atualizar<K extends keyof DadosEmpresa>(campo: K, valor: string): void {
    setDados((d) => ({ ...d, [campo]: valor.trim() === '' ? null : valor }));
  }

  /** Aplica máscara 00000-000 enquanto o usuário digita. */
  function mascararCep(valor: string): string {
    const digitos = valor.replace(/\D/g, '').slice(0, 8);
    if (digitos.length <= 5) return digitos;
    return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
  }

  /** Máscara CNPJ: 00.000.000/0000-00 (14 dígitos). Aplicação progressiva. */
  function mascararCnpj(valor: string): string {
    const d = valor.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12)
      return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }

  /**
   * Máscara de telefone BR. 10 dígitos → (XX) XXXX-XXXX (fixo).
   * 11 dígitos → (XX) XXXXX-XXXX (celular com 9). Durante a digitação
   * mostra o formato parcial mais próximo.
   */
  function mascararTelefone(valor: string): string {
    const d = valor.replace(/\D/g, '').slice(0, 11);
    if (d.length === 0) return '';
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }

  /**
   * Consulta o BrasilAPI e preenche endereço/cidade/UF. Não sobrescreve
   * o que o usuário já digitou — só atualiza campos vazios.
   * Fonte: https://brasilapi.com.br/api/cep/v2/{cep}
   */
  async function buscarCep(): Promise<void> {
    const digitos = (dados.cep ?? '').replace(/\D/g, '');
    if (digitos.length !== 8) {
      onToast({ mensagem: 'Informe um CEP com 8 dígitos.', tipo: 'erro' });
      return;
    }
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://brasilapi.com.br/api/cep/v2/${digitos}`);
      if (!resp.ok) {
        onToast({
          mensagem: resp.status === 404 ? 'CEP não encontrado.' : `Erro ${resp.status} ao consultar CEP.`,
          tipo: 'erro',
        });
        return;
      }
      const corpo = (await resp.json()) as {
        street?: string;
        neighborhood?: string;
        city?: string;
        state?: string;
      };
      // Buscar CEP sobrescreve os campos de endereço com o que veio da
      // BrasilAPI — assumindo que o usuário clicou em "Buscar" justamente
      // pra atualizar. Mantém o valor anterior só quando a API não retorna
      // aquele campo específico (ex.: zona rural sem street).
      setDados((d) => ({
        ...d,
        endereco: corpo.street?.trim() ? corpo.street : d.endereco,
        bairro: corpo.neighborhood?.trim() ? corpo.neighborhood : d.bairro,
        cidade: corpo.city?.trim() ? corpo.city : d.cidade,
        estado: corpo.state?.trim() ? corpo.state : d.estado,
      }));
      onToast({ mensagem: 'Endereço preenchido a partir do CEP.', tipo: 'sucesso' });
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao consultar CEP.',
        tipo: 'erro',
      });
    } finally {
      setBuscandoCep(false);
    }
  }

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await configuracoesApi.salvarEmpresa(dados);
      setDados(r);
      setOriginal(r);
      onToast({ mensagem: 'Dados da empresa salvos.', tipo: 'sucesso' });
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao salvar.',
        tipo: 'erro',
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Dados da empresa</span>
      </div>
      <div className="card__body">
        <p className="hint" style={{ marginTop: 0 }}>
          Esses dados aparecem no cabeçalho das impressões A4 e cupom 58mm.
          Salvos localmente e sincronizados com o servidor — quando você
          loga em outra máquina com a mesma conta, os dados aparecem lá
          automaticamente.
        </p>

        {carregando ? (
          <p className="hint">Carregando…</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            <div style={{ gridColumn: '1 / -1' }}>
              <Field
                rotulo="Nome da empresa"
                dica="É o mesmo nome que aparece no Menu Usuário (acima do plano). Editar aqui atualiza a conta."
              >
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    type="text"
                    value={dados.nomeEmpresa ?? ''}
                    onChange={(e) => atualizar('nomeEmpresa', e.target.value)}
                    maxLength={120}
                  />
                )}
              </Field>
            </div>
            <Field rotulo="Telefone">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="tel"
                  inputMode="numeric"
                  value={dados.telefone ?? ''}
                  onChange={(e) => atualizar('telefone', mascararTelefone(e.target.value))}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              )}
            </Field>
            <Field rotulo="E-mail">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="email"
                  value={dados.email ?? ''}
                  onChange={(e) => atualizar('email', e.target.value)}
                  maxLength={254}
                />
              )}
            </Field>
            <Field rotulo="CNPJ">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  inputMode="numeric"
                  value={dados.cnpj ?? ''}
                  onChange={(e) => atualizar('cnpj', mascararCnpj(e.target.value))}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              )}
            </Field>
            <Field rotulo="Inscrição Estadual">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  value={dados.inscricaoEstadual ?? ''}
                  onChange={(e) => atualizar('inscricaoEstadual', e.target.value)}
                  maxLength={40}
                />
              )}
            </Field>
            <Field rotulo="CEP" dica="Buscar preenche endereço, cidade e UF.">
              {(id) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    id={id}
                    className="input"
                    type="text"
                    inputMode="numeric"
                    value={dados.cep ?? ''}
                    onChange={(e) => atualizar('cep', mascararCep(e.target.value))}
                    placeholder="00000-000"
                    maxLength={9}
                  />
                  <Button
                    onClick={() => void buscarCep()}
                    disabled={buscandoCep || (dados.cep ?? '').replace(/\D/g, '').length !== 8}
                  >
                    {buscandoCep ? 'Buscando…' : 'Buscar'}
                  </Button>
                </div>
              )}
            </Field>
            <div /> {/* espaçador pra manter o grid de 2 colunas alinhado */}
            <Field rotulo="Endereço">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  value={dados.endereco ?? ''}
                  onChange={(e) => atualizar('endereco', e.target.value)}
                  maxLength={255}
                />
              )}
            </Field>
            <Field rotulo="Número">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  value={dados.numero ?? ''}
                  onChange={(e) => atualizar('numero', e.target.value)}
                  maxLength={20}
                  placeholder="Ex.: 123 ou S/N"
                />
              )}
            </Field>
            <Field rotulo="Bairro">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  value={dados.bairro ?? ''}
                  onChange={(e) => atualizar('bairro', e.target.value)}
                  maxLength={120}
                />
              )}
            </Field>
            <Field rotulo="Cidade">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  value={dados.cidade ?? ''}
                  onChange={(e) => atualizar('cidade', e.target.value)}
                  maxLength={120}
                />
              )}
            </Field>
            <Field rotulo="UF">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  value={dados.estado ?? ''}
                  onChange={(e) => atualizar('estado', e.target.value.toUpperCase())}
                  maxLength={2}
                  style={{ textTransform: 'uppercase' }}
                />
              )}
            </Field>
            <div /> {/* espaçador pra fechar o grid */}
          </div>
        )}

        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button
            onClick={() => setDados(original)}
            disabled={!alterou || salvando}
          >
            Descartar
          </Button>
          <Button
            variante="primary"
            onClick={() => void salvar()}
            disabled={!alterou || salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar dados'}
          </Button>
        </div>
      </div>
    </div>
  );
}
